import React, { useState, useEffect } from "react";
import DesktopSidebar from "./DesktopSidebar";
import MobileSidebar from "./MobileSidebar";
import { useAuth } from "@/modules/auth";
import { organizationApi } from "@/modules/organization/services/organizationApi";

const ORGANIZATION_LOGO_VERSION_KEY = "organization_logo_version";

const appendLogoVersion = (logoUrl?: string | null, version?: number | null) => {
  if (!logoUrl) {
    return null;
  }

  if (!version) {
    return logoUrl;
  }

  const separator = logoUrl.includes("?") ? "&" : "?";
  return `${logoUrl}${separator}v=${version}`;
};

interface SidebarProps {
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isCollapsed, onToggleCollapse }) => {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [organizationName, setOrganizationName] = useState<string | null>(null);
  const [organizationLogoUrl, setOrganizationLogoUrl] = useState<string | null>(
    null
  );
  const [logoVersion, setLogoVersion] = useState<number | null>(() => {
    const savedVersion = localStorage.getItem(ORGANIZATION_LOGO_VERSION_KEY);
    const parsedVersion = savedVersion ? Number(savedVersion) : NaN;
    return Number.isFinite(parsedVersion) ? parsedVersion : null;
  });
  const { isAuthenticated, user } = useAuth();

  // Auto-colapsar en pantallas pequeñas
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1280 && !isCollapsed) {
        onToggleCollapse();
      }
    };

    if (window.innerWidth < 1280 && !isCollapsed) {
      onToggleCollapse();
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isCollapsed, onToggleCollapse]);

  useEffect(() => {
    let isMounted = true;

    const loadOrganizationBranding = async () => {
      if (!isAuthenticated) {
        setOrganizationName(null);
        setOrganizationLogoUrl(null);
        return;
      }

      if (user?.organizationName) {
        setOrganizationName(user.organizationName);
      }

      try {
        const organization = await organizationApi.getOrganization();
        if (!isMounted) return;

        setOrganizationName(
          organization.name || user?.organizationName || null
        );
        setOrganizationLogoUrl(organization.logoUrl || null);

        const savedVersion = localStorage.getItem(ORGANIZATION_LOGO_VERSION_KEY);
        const parsedVersion = savedVersion ? Number(savedVersion) : NaN;
        setLogoVersion(Number.isFinite(parsedVersion) ? parsedVersion : null);
      } catch {
        if (!isMounted) return;
        setOrganizationName(user?.organizationName || null);
        setOrganizationLogoUrl(null);
      }
    };

    loadOrganizationBranding();

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, user?.organizationName]);

  useEffect(() => {
    const handleLogoUpdated = (
      event: Event
    ) => {
      const customEvent = event as CustomEvent<{
        logoUrl?: string | null;
        version?: number;
      }>;

      if (customEvent.detail?.logoUrl !== undefined) {
        setOrganizationLogoUrl(customEvent.detail.logoUrl || null);
      }

      if (customEvent.detail?.version) {
        setLogoVersion(customEvent.detail.version);
      } else {
        setLogoVersion(Date.now());
      }
    };

    const handleStorageUpdate = (event: StorageEvent) => {
      if (event.key !== ORGANIZATION_LOGO_VERSION_KEY || !event.newValue) {
        return;
      }

      const parsedVersion = Number(event.newValue);
      if (Number.isFinite(parsedVersion)) {
        setLogoVersion(parsedVersion);
      }
    };

    window.addEventListener("organization-logo-updated", handleLogoUpdated);
    window.addEventListener("storage", handleStorageUpdate);

    return () => {
      window.removeEventListener("organization-logo-updated", handleLogoUpdated);
      window.removeEventListener("storage", handleStorageUpdate);
    };
  }, []);

  const logoUrlWithVersion = appendLogoVersion(organizationLogoUrl, logoVersion);

  return (
    <>
      <DesktopSidebar
        isCollapsed={isCollapsed}
        onToggleCollapse={onToggleCollapse}
        organizationName={organizationName}
        organizationLogoUrl={logoUrlWithVersion}
      />
      <MobileSidebar
        isMobileOpen={isMobileOpen}
        setIsMobileOpen={setIsMobileOpen}
        organizationName={organizationName}
        organizationLogoUrl={logoUrlWithVersion}
      />
    </>
  );
};

export default Sidebar;
